import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { AxiosError, AxiosInstance } from 'axios';

import { mapTimesheetApiEntityToTimesheet } from '#/shared/adapters/mapTimesheetApiEntityToTimesheet';
import { SetTimesheetDataProps, useTimesheetStore } from '#/shared/stores/timesheetStore';
import { Timesheet } from '#/shared/types/Timesheet';
import { TimesheetApiEntity } from '#/shared/types/TimesheetApiModal';

import { useAxiosConfig } from './useAxiosConfig';
import { ErrorInfo, useMonitoring } from './useMonitoring';

export type GetFetchTimesheetQueryKeyProps = {
  jwtToken: string;
};

export const getFetchTimesheetQueryKey = ({ jwtToken }: GetFetchTimesheetQueryKeyProps) => {
  return ['jwtToken', { jwtToken }];
};

export const fetchTimesheet = async ({
  url,
  api,
  noticeError,
  saveTimesheetToStore,
}: {
  url: string;
  api: AxiosInstance;
  noticeError: (error: Error, info?: ErrorInfo | undefined) => void;
  saveTimesheetToStore: (data: SetTimesheetDataProps) => void;
}) => {
  const { data } = await api.get<TimesheetApiEntity>(url).catch(error => {
    saveTimesheetToStore({ timesheet: null, links: null });

    // This is likely to be an expired token/approved timesheet, no need to log it
    if (error.response?.status === 400 || error.response?.status === 401) {
      throw error;
    }

    noticeError(new Error('Failed to fetch timesheet from API'), {
      originalException: error,
      originalUrl: url,
    });

    throw error;
  });

  try {
    const timesheet = mapTimesheetApiEntityToTimesheet(data);
    saveTimesheetToStore({ timesheet, links: data.links });
    return timesheet;
  } catch (error) {
    noticeError(new Error('Failed to map timesheet API entity to timesheet after fetching timesheet'), {
      originalException: error,
      originalTimesheetData: data,
      originalUrl: url,
    });

    saveTimesheetToStore({ timesheet: null, links: null });

    throw error;
  }
};

type FetchTimesheetParams = {
  jwtToken: string;
  timesheet?: Timesheet;
};

/**
 * Hook for fetching specific timesheet data
 */
export const useFetchTimesheet = ({
  jwtToken,
  timesheet,
}: FetchTimesheetParams): UseQueryResult<Timesheet, AxiosError> => {
  const api = useAxiosConfig();
  const { noticeError } = useMonitoring();
  const url = '/timesheet-online-approval/timesheet';

  const saveTimesheetToStore = useTimesheetStore(state => state.setTimesheetData);

  return useQuery({
    queryKey: getFetchTimesheetQueryKey({ jwtToken }),
    queryFn: async () => await fetchTimesheet({ url, api, noticeError, saveTimesheetToStore }),
    initialData: timesheet,
    enabled: !!jwtToken,
    staleTime: Infinity, // Setting it to infinity to avoid refetching
    retry: false,
  });
};
